/**
 * Volume evaluation utility for Steve Render Service.
 *
 * Volume can be a number, undefined, or a function of frame.
 * This normalizes it to a clamped numeric value.
 *
 * Written from scratch for Steve.AI.
 */

/**
 * Evaluate the volume prop at a given frame.
 *
 * @param {object} params
 * @param {number|function|undefined} params.volume
 * @param {number} params.frame
 * @param {number} [params.mediaVolume=1]
 * @param {boolean} [params.allowAmplificationDuringRender=false]
 * @returns {number} Clamped volume value (0 to maxVolume)
 */
export const evaluateVolume = ({
  frame,
  volume,
  mediaVolume = 1,
  allowAmplificationDuringRender = false,
}) => {
  const maxVolume = allowAmplificationDuringRender ? Infinity : 1;

  if (typeof volume === 'undefined') {
    return Number(mediaVolume);
  }

  if (typeof volume === 'number') {
    return Math.min(maxVolume, volume * mediaVolume);
  }

  if (typeof volume === 'function') {
    const evaluated = volume(frame) * mediaVolume;

    if (typeof evaluated !== 'number') {
      throw new TypeError(
        `Volume function must return a number. Got ${typeof evaluated} at frame ${frame}`
      );
    }
    if (Number.isNaN(evaluated)) {
      throw new TypeError(`Volume function returned NaN at frame ${frame}`);
    }
    if (!Number.isFinite(evaluated)) {
      throw new TypeError(`Volume function returned non-finite value at frame ${frame}`);
    }

    return Math.max(0, Math.min(maxVolume, evaluated));
  }

  return Number(mediaVolume);
};
